import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, AlertTriangle, MapPin, FileText, Hammer, User, Calendar, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Public read-only verify page — no auth required.
 * URL patterns:
 *   /verify/handover/{cert_ref}
 *   /verify/inspection/{report_no}
 *   /verify/rams/{doc_ref}
 *   /verify/structure/{structure_ref}
 * Powers the QR codes printed on every certificate + scaff tag.
 */
export default function Verify() {
  const { kind, ref } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND}/api/verify/${kind}/${encodeURIComponent(ref)}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || "Not found"); });
    return () => { cancelled = true; };
  }, [kind, ref]);

  if (err) return <VerifyError kind={kind} ref_={ref} detail={err} />;
  if (!data) return <VerifyLoading />;

  return (
    <VerifyShell kind={kind} ref_={ref}>
      {kind === "handover"   ? <HandoverBody d={data} /> :
       kind === "inspection" ? <InspectionBody d={data} /> :
       kind === "rams"       ? <RamsBody d={data} /> :
       kind === "structure"  ? <StructureBody d={data} /> :
                               <UnknownKind kind={kind} />}
    </VerifyShell>
  );
}

/* ---------- shell / header ---------- */
function VerifyShell({ children, kind, ref_ }) {
  return (
    <div data-testid="verify-page" style={{ minHeight: "100vh", background: "#0f172a", color: "#f1f5f9", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif" }}>
      <header style={{ background: "#020617", borderBottom: "3px solid #ff5a00", padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
        <img src="/brand/newell-logo-admin.png" alt="Newell Scaffolding" style={{ height: 32 }} data-testid="verify-logo" />
        <div style={{ flex: 1 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#ff5a00", fontWeight: 700 }}>NEWELL SCAFFOLDING · LIVE VERIFY</div>
          <div style={{ fontSize: 13, color: "#cbd5e1" }}>Read-only audit chain · McCain Whittlesey</div>
        </div>
        <div className="mono" style={{ fontSize: 10, textAlign: "right", color: "#94a3b8" }}>
          {kind?.toUpperCase()}<br />{ref_}
        </div>
      </header>
      <div style={{ padding: "24px 20px", maxWidth: 720, margin: "0 auto" }}>{children}</div>
      <footer style={{ padding: "20px", textAlign: "center", color: "#475569", fontSize: 11 }}>
        <ShieldCheck size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
        This page is the source-of-truth for the certificate you scanned. Any printed PDF disagreeing with this record has been altered.
      </footer>
    </div>
  );
}

function VerifyLoading() {
  return (
    <VerifyShell kind="" ref_="">
      <div style={{ textAlign: "center", padding: 40, color: "#94a3b8" }} data-testid="verify-loading">Fetching audit record…</div>
    </VerifyShell>
  );
}

function VerifyError({ kind, ref_, detail }) {
  return (
    <VerifyShell kind={kind} ref_={ref_}>
      <div data-testid="verify-error" className="card" style={{ background: "#450a0a", border: "1px solid #dc2626", padding: 20, borderRadius: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <AlertTriangle color="#fca5a5" />
          <h1 style={{ margin: 0, fontSize: 20, color: "#fca5a5" }}>Record not found</h1>
        </div>
        <p style={{ color: "#fecaca", fontSize: 14, margin: 0 }}>{detail}</p>
        <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 12 }}>
          The reference <code style={{ background: "#0f172a", padding: "2px 6px", borderRadius: 2 }}>{ref_}</code> is not in our records. If you scanned a printed tag, the tag may pre-date this system or be counterfeit.
        </p>
      </div>
    </VerifyShell>
  );
}

function UnknownKind({ kind }) {
  return <div style={{ color: "#f87171" }} data-testid="verify-unknown-kind">Unknown verify kind: {kind}</div>;
}

/* ---------- helpers ---------- */
const Card = ({ children, testid }) => (
  <div data-testid={testid} className="card" style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 4, padding: 18, marginBottom: 14 }}>{children}</div>
);
const Row = ({ label, value, mono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px dashed #334155", fontSize: 13 }}>
    <span className="mono" style={{ color: "#94a3b8", letterSpacing: "0.08em", fontSize: 10 }}>{label.toUpperCase()}</span>
    <span className={mono ? "mono" : ""} style={{ color: "#f1f5f9", fontWeight: 600, textAlign: "right" }}>{value || "—"}</span>
  </div>
);
const StatusBadge = ({ label, tone }) => {
  const tones = {
    green:  { bg: "#065f46", fg: "#d1fae5" },
    amber:  { bg: "#78350f", fg: "#fde68a" },
    red:    { bg: "#7f1d1d", fg: "#fecaca" },
    blue:   { bg: "#1e3a8a", fg: "#dbeafe" },
    slate:  { bg: "#334155", fg: "#cbd5e1" },
  }[tone] || { bg: "#334155", fg: "#cbd5e1" };
  return <span style={{ background: tones.bg, color: tones.fg, padding: "3px 10px", borderRadius: 3, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>{label}</span>;
};
const fmt = (v) => (v || "").toString().slice(0, 19).replace("T", " ");

/* ---------- handover ---------- */
function HandoverBody({ d }) {
  return (
    <>
      <Card testid="verify-handover-hero">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#94a3b8" }}>HANDOVER CERTIFICATE</div>
            <h1 style={{ margin: "4px 0", fontSize: 22, color: "#fff" }}>{d.cert_ref}</h1>
          </div>
          <StatusBadge label={d.handover_status || "ISSUED"} tone={d.handover_status === "Complete" ? "green" : "amber"} />
        </div>
        <div style={{ color: "#cbd5e1", fontSize: 13 }}>
          Issued {fmt(d.issued_at)} UTC by {d.newell_print_name || "—"} ({d.newell_position || "—"})
        </div>
      </Card>
      <Card testid="verify-handover-structure">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}><Hammer size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Structure</h3>
        <Row label="Ref"          value={d.structure?.structure_ref || "—"} mono />
        <Row label="Area"         value={d.structure?.area || "—"} />
        <Row label="Description"  value={d.structure?.description || "—"} />
        <Row label="Duty class"   value={d.duty_class} />
        <Row label="Max load"     value={d.max_load_kn_m2 ? `${d.max_load_kn_m2} kN/m²` : "—"} />
        <Row label="TG20:21"      value={d.tg20_compliant ? "COMPLIANT" : (d.bespoke_design_ref ? `Bespoke · ${d.bespoke_design_ref}` : "NO")} />
      </Card>
      <Card testid="verify-handover-signatures">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}><User size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Signatures</h3>
        <div style={{ padding: "10px 0" }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "#94a3b8" }}>NEWELL COMPETENT PERSON</div>
          <div style={{ fontWeight: 700, marginTop: 2 }}>{d.newell_print_name || "—"}</div>
          <div style={{ color: "#cbd5e1", fontSize: 12 }}>{d.newell_position} · CISRS {d.newell_cisrs_ref || "—"}</div>
          <div className="mono" style={{ color: "#a7f3d0", fontSize: 11, marginTop: 4 }}><CheckCircle2 size={11} style={{ verticalAlign: -2, marginRight: 4 }} />Signed {fmt(d.newell_signed_at)} UTC</div>
        </div>
        {d.mccain_receipt ? (
          <div style={{ padding: "10px 0", borderTop: "1px dashed #334155" }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "#94a3b8" }}>MCCAIN COUNTERSIGN</div>
            <div style={{ fontWeight: 700, marginTop: 2 }}>{d.mccain_receipt.print_name || "—"}</div>
            <div style={{ color: "#cbd5e1", fontSize: 12 }}>{d.mccain_receipt.position || "—"}</div>
            <div className="mono" style={{ color: "#a7f3d0", fontSize: 11, marginTop: 4 }}><CheckCircle2 size={11} style={{ verticalAlign: -2, marginRight: 4 }} />Countersigned {fmt(d.mccain_receipt.countersigned_at)} UTC</div>
          </div>
        ) : (
          <div style={{ padding: "10px 0", borderTop: "1px dashed #334155", color: "#fbbf24", fontSize: 12 }}>Awaiting McCain countersignature</div>
        )}
      </Card>
      <Card testid="verify-handover-photos">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}><FileText size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Evidence photos</h3>
        <div style={{ color: "#cbd5e1", fontSize: 13 }}>{d.evidence_photo_count} evidence photograph(s) attached — see the PDF for the images.</div>
      </Card>
    </>
  );
}

/* ---------- inspection ---------- */
function InspectionBody({ d }) {
  const s = d.overall_status || "";
  const tone = s.toLowerCase().startsWith("safe") ? "green" : s.toLowerCase().startsWith("cond") ? "amber" : "red";
  return (
    <>
      <Card testid="verify-inspection-hero">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#94a3b8" }}>INSPECTION REPORT</div>
            <h1 style={{ margin: "4px 0", fontSize: 22, color: "#fff" }}>{d.report_no}</h1>
          </div>
          <StatusBadge label={d.overall_status || "PENDING"} tone={tone} />
        </div>
        <div style={{ color: "#cbd5e1", fontSize: 13 }}>Inspected {fmt(d.inspected_at)} UTC · Next due {fmt(d.next_due_at) || "—"}</div>
      </Card>
      <Card testid="verify-inspection-summary">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}>Item summary</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
          <StatBox n={d.item_summary?.total || 0}  label="TOTAL" tone="slate" />
          <StatBox n={d.item_summary?.green || 0}  label="GREEN" tone="green" />
          <StatBox n={d.item_summary?.amber || 0}  label="AMBER" tone="amber" />
          <StatBox n={d.item_summary?.unsafe || 0} label="UNSAFE" tone="red" />
        </div>
      </Card>
      <Card testid="verify-inspection-structure">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}><Hammer size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Structure</h3>
        <Row label="Ref"          value={d.structure?.structure_ref || "—"} mono />
        <Row label="Area"         value={d.structure?.area || "—"} />
        <Row label="Description"  value={d.structure?.description || "—"} />
      </Card>
      <Card testid="verify-inspection-signatures">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}><User size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Signatures</h3>
        <div style={{ padding: "10px 0" }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "#94a3b8" }}>INSPECTOR</div>
          <div style={{ fontWeight: 700, marginTop: 2 }}>{d.inspector_print_name || "—"}</div>
          <div style={{ color: "#cbd5e1", fontSize: 12 }}>{d.inspector_position} · CISRS {d.inspector_cisrs_ref || "—"}</div>
          <div className="mono" style={{ color: "#a7f3d0", fontSize: 11, marginTop: 4 }}><CheckCircle2 size={11} style={{ verticalAlign: -2, marginRight: 4 }} />Signed {fmt(d.inspector_signed_at)} UTC</div>
        </div>
        {d.client_receipt ? (
          <div style={{ padding: "10px 0", borderTop: "1px dashed #334155" }}>
            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "#94a3b8" }}>CLIENT RECEIPT</div>
            <div style={{ fontWeight: 700, marginTop: 2 }}>{d.client_receipt.print_name || "—"}</div>
            <div style={{ color: "#cbd5e1", fontSize: 12 }}>{d.client_receipt.position || "—"}</div>
            <div className="mono" style={{ color: "#a7f3d0", fontSize: 11, marginTop: 4 }}><CheckCircle2 size={11} style={{ verticalAlign: -2, marginRight: 4 }} />Signed {fmt(d.client_receipt.signed_at)} UTC</div>
          </div>
        ) : null}
      </Card>
    </>
  );
}
function StatBox({ n, label, tone }) {
  const bg = { slate: "#334155", green: "#065f46", amber: "#78350f", red: "#7f1d1d" }[tone] || "#334155";
  return (
    <div style={{ background: bg, padding: 8, borderRadius: 3, textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{n}</div>
      <div className="mono" style={{ fontSize: 9, letterSpacing: "0.08em", color: "#e2e8f0" }}>{label}</div>
    </div>
  );
}

/* ---------- rams ---------- */
function RamsBody({ d }) {
  return (
    <>
      <Card testid="verify-rams-hero">
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#94a3b8" }}>RAMS DOCUMENT</div>
        <h1 style={{ margin: "4px 0", fontSize: 22, color: "#fff" }}>{d.doc_ref || d.id?.slice(0, 8)}</h1>
        <div style={{ color: "#cbd5e1", fontSize: 13 }}>{d.title || "—"}</div>
        <div style={{ marginTop: 8 }}><StatusBadge label={d.status || "DRAFT"} tone={d.status === "DRASigned" ? "green" : d.status === "Stage2Reviewed" ? "amber" : "slate"} /></div>
      </Card>
      <Card testid="verify-rams-signatures">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}>Sign-off chain</h3>
        {["stage1_sign", "stage2_review", "dra_sign"].map((k) => {
          const stage = d[k];
          const stageLabel = k === "stage1_sign" ? "STAGE 1 (COMPETENT PERSON)" : k === "stage2_review" ? "STAGE 2 (REVIEWER)" : "DRA SIGN-OFF";
          return (
            <div key={k} style={{ padding: "8px 0", borderBottom: "1px dashed #334155" }}>
              <div className="mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "#94a3b8" }}>{stageLabel}</div>
              {stage ? (
                <>
                  <div style={{ fontWeight: 700, marginTop: 2 }}>{stage.by_name || "—"}</div>
                  <div className="mono" style={{ color: "#a7f3d0", fontSize: 11 }}><CheckCircle2 size={11} style={{ verticalAlign: -2, marginRight: 4 }} />Signed {fmt(stage.at)} UTC</div>
                </>
              ) : (
                <div style={{ color: "#94a3b8", fontSize: 12 }}>Not yet signed</div>
              )}
            </div>
          );
        })}
      </Card>
    </>
  );
}

/* ---------- structure (scaff-tag scan) ---------- */
function StructureBody({ d }) {
  const latestInsp = (d.inspections || [])[0] || null;
  const s = latestInsp?.overall_status || "";
  const tone = s.toLowerCase().startsWith("safe") ? "green" : s.toLowerCase().startsWith("cond") ? "amber" : "red";
  return (
    <>
      <Card testid="verify-structure-hero">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#94a3b8" }}>SCAFFOLD STRUCTURE</div>
            <h1 style={{ margin: "4px 0", fontSize: 22, color: "#fff" }}>{d.structure_ref}</h1>
            <div style={{ color: "#cbd5e1", fontSize: 13 }}><MapPin size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{d.area || "—"}</div>
          </div>
          <StatusBadge label={latestInsp?.overall_status || d.status || "—"} tone={tone} />
        </div>
        <div style={{ color: "#cbd5e1", fontSize: 13, marginTop: 8 }}>{d.description || "—"}</div>
      </Card>
      <Card testid="verify-structure-spec">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}>Specification</h3>
        <Row label="Type"   value={d.scaffold_type} />
        <Row label="Duty"   value={d.duty_class} />
        <Row label="Max load" value={d.max_load_kn_m2 ? `${d.max_load_kn_m2} kN/m²` : "—"} />
        <Row label="TG20:21" value={d.tg20_compliant ? "COMPLIANT" : "BESPOKE DESIGN"} />
      </Card>
      <Card testid="verify-structure-inspections">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}><Calendar size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Inspections ({d.inspections?.length || 0})</h3>
        {(d.inspections || []).length === 0 ? <div style={{ color: "#94a3b8", fontSize: 12 }}>No inspections recorded.</div> :
          (d.inspections || []).map((i, idx) => (
            <div key={idx} data-testid={`verify-structure-inspection-${idx}`} style={{ padding: "8px 0", borderBottom: "1px dashed #334155" }}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                <div className="mono" style={{ color: "#f1f5f9", fontWeight: 700 }}>{i.report_no || `#${idx + 1}`}</div>
                <StatusBadge label={i.overall_status || "—"} tone={(i.overall_status || "").toLowerCase().startsWith("safe") ? "green" : (i.overall_status || "").toLowerCase().startsWith("cond") ? "amber" : "red"} />
              </div>
              <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                {fmt(i.inspected_at)} · {i.inspector_name || "—"} · next due {fmt(i.next_due_at) || "—"}
              </div>
            </div>
          ))
        }
      </Card>
      <Card testid="verify-structure-handovers">
        <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#cbd5e1" }}><FileText size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Handovers ({d.handovers?.length || 0})</h3>
        {(d.handovers || []).length === 0 ? <div style={{ color: "#94a3b8", fontSize: 12 }}>No handovers recorded.</div> :
          (d.handovers || []).map((h, idx) => (
            <div key={idx} data-testid={`verify-structure-handover-${idx}`} style={{ padding: "8px 0", borderBottom: "1px dashed #334155", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
              <div>
                <div className="mono" style={{ color: "#f1f5f9", fontWeight: 700 }}>{h.cert_ref || `#${idx + 1}`}</div>
                <div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{fmt(h.issued_at)} · {h.newell_print_name || "—"}</div>
              </div>
              <StatusBadge label={h.handover_status || "ISSUED"} tone={h.handover_status === "Complete" ? "green" : "amber"} />
            </div>
          ))
        }
      </Card>
    </>
  );
}
